/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useRef, useEffect } from 'react';

import Autosuggest from 'react-autosuggest';
import { Accordion, CheckboxInputGroupAccessibleWDP, Button } from '@components';
import { iSponsor } from '@interfaces/interfaces';
import Checkbox from '@components/CheckboxInput/Checkbox';

import SearchResults from './SearchResults';
import styles from './SearchBox.module.scss';
import { ReactComponent as DropdownIcon } from '../../images/dropdown.svg';
import { ReactComponent as SearchIcon } from '../../images/search.svg';
import theme from './Autosuggest.theme.module.scss';

interface iSponsorInfoData {
  data: iSponsor[];
  searchFn: any;
  getSuggestions: any;
  getSuggestionValue: any;
  renderSuggestion: any;
}

const NO_RESULTS_ERROR_MESSAGE = 'No sponsor found for your search. Try another search using different details or adjust your filters.';

const SearchBox = ({ data, searchFn, getSuggestions, getSuggestionValue, renderSuggestion }: iSponsorInfoData): any => {
  const searchResultRef = useRef<HTMLDivElement>(null);
  const focusRef = useRef<HTMLInputElement>(null);
  const activityDropdownRef = useRef<HTMLInputElement>(null);
  const serviceDropdownRef = useRef<HTMLInputElement>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchedVal, setSearchedVal] = useState('');
  const [searchedData, setSearchedData] = useState<iSponsor[]>([]);
  const [filteredData, setFilteredData] = useState<iSponsor[]>([]);
  const [selectedActivities, setSelectedActivities] = useState<string[]>([]);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [showActivitiesDropdown, setShowActivitiesDropdown] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [showSearchResult, setshowSearchResult] = useState(false);
  const [showFilterResult, setShowFilterResult] = useState(false);
  const [telehealthOnly, setTelehealthOnly] = useState(false);
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState<iSponsor[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(false);

  const [clearButtonClicked, setClearButtonClicked] = useState(false);

  const loadingTime = 500;

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= 768);
    }
    // Initial check on mount
    handleResize();

    // Listen for window resize events
    window.addEventListener('resize', handleResize);
    const handleClickOutside = (e: any) => {
      if (activityDropdownRef.current && !activityDropdownRef.current.contains(e.target) && serviceDropdownRef.current && !serviceDropdownRef.current.contains(e.target)) {
        setShowActivitiesDropdown(false);
        setShowServicesDropdown(false);
      }
    };
    const handleEscKey = (e: any) => {
      if (e.key === 'Escape') {
        setShowActivitiesDropdown(false);
        setShowServicesDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const applyFilters = (dataToFilter = searchedData, timeToLoad:number) => {
    setLoading(true);
    setTimeout(() => {
      let filtered = dataToFilter;
      if (selectedActivities.length > 0) {
        filtered = filtered.filter((item) => item.Activities.some((act) => selectedActivities.includes(act)));
      }
      if (selectedServices.length > 0) {
        filtered = filtered.filter((item) => item.Services.some((act) => selectedServices.includes(act)));
      }

      if (telehealthOnly) {
        filtered = filtered.filter((item) => item.Telehealth.toLocaleLowerCase() === 'yes');
      }

      setFilteredData(filtered);
      setShowFilterResult(true);
      setshowSearchResult(false);
      // setShowActivitiesDropdown(false);
      // setShowServicesDropdown(false);
      setLoading(false);
    }, timeToLoad);
  };

  const handleSearch = (searchInput: string) => {
    setLoading(true);
    setTimeout(() => {
      setError('');
      setSearchedVal(searchInput);
      let results = searchFn(searchInput);
      setSearchedData(results);
      setShowFilterResult(false);
      setshowSearchResult(true);
      if (!searchInput.trim()) {
        results = data;
        setSearchedData(data);
      }
      if (selectedActivities.length > 0 || selectedServices.length > 0 || telehealthOnly) {
        applyFilters(results, 0);
      }
      focusRef.current?.blur();
      if (isMobile) {
        setTimeout(() => {
          searchResultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
      }
      setLoading(false);
      setShowActivitiesDropdown(false);
      setShowServicesDropdown(false);
    }, loadingTime);
  };

  useEffect(() => {
    if (searchedData.length > 0 && !clearButtonClicked) applyFilters(searchedData, loadingTime);
  }, [selectedActivities, selectedServices, telehealthOnly]);

  const allActivities = Array.from(new Set(data.flatMap((item) => item.Activities)));
  const allServices = Array.from(new Set(data.flatMap((item) => item.Services)));

  const onSuggestionsFetchRequested = ({ value }: { value: string }) => {
    getSuggestions(value, setSuggestions);
  };

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  // update search query = suggestion
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onSuggestionSelected = async (event: any, { suggestion }: { suggestion: iSponsor }) => {
    const suggestionOrgName = suggestion.OrgName;
    if (suggestionOrgName) {
      handleSearch(suggestionOrgName);
    }
  };

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      handleSearch(searchQuery);
    }
  };

  const inputProps = {
    placeholder: 'Enter a sponsor name',
    value: searchQuery,
    onChange: (event: any, { newValue }: { newValue: string }) => {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = newValue;
      const decodedSuggestionOrgName = textarea.value;
      setSearchQuery(decodedSuggestionOrgName);
    },
    onKeyDown: handleKeyDown,
    ref: focusRef,
  };

  const clearButtonClick = () => {
    setClearButtonClicked(true);
    if (selectedActivities.length > 0 || telehealthOnly || selectedServices.length > 0) {
      // setSearchedData([]);
      setSelectedActivities([]);
      setSelectedServices([]);
      setSearchQuery('');
      setTelehealthOnly(false);
      setShowActivitiesDropdown(false);
      setShowServicesDropdown(false);
    }
    if (searchQuery) {
      setSearchQuery('');
    }
    if (focusRef.current) {
      focusRef.current.setAttribute('tabindex', '-1');
      focusRef.current.focus();
    }
    setTimeout(() => setClearButtonClicked(false), 0);
  };

  return (
    <div>
      <div className={styles.searchContainer}>
        <div className={styles.searchTitle}>Search by sponsor</div>
        <div className={styles.searchBoxContainer}>
          <div id="sponsor-search-input" className={styles.autosuggestContainer} tabIndex={-1}>
            <div className={styles.autosuggest}>
              <SearchIcon className={styles.searchIcon} />
              <Autosuggest
                suggestions={suggestions}
                onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                onSuggestionsClearRequested={onSuggestionsClearRequested}
                onSuggestionSelected={onSuggestionSelected}
                getSuggestionValue={getSuggestionValue}
                renderSuggestion={renderSuggestion}
                inputProps={inputProps}
                theme={theme}
              />
            </div>
          </div>
          <div id="sponsor-search-button" className={styles.searchButtonContainer}>
            <Button size="small" onClick={() => handleSearch(searchQuery)}>
              Search
            </Button>
          </div>
        </div>
        {isMobile ? (
          <div className={styles.accordionContainer}>
            <Accordion
              title="Filter search results"
              variant="wdp"
              popupContent={(
                <div className={styles.filterBoxContainer}>
                  <div className={styles.filtersContainer}>
                    <div className={styles.filterContainer}>
                      <div className={styles.searchTitle3}>Type of activities they offer:</div>
                      <div ref={activityDropdownRef} id="sponsor-activities-dropdown" className={styles.selectContainer}>
                        <span
                          className={styles.checkboxSelect}
                          id="activity"
                          title="Activity"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === ' ' || e.key === 'Enter') {
                              setShowActivitiesDropdown(!showActivitiesDropdown);
                            }
                          }}
                          onClick={() => setShowActivitiesDropdown(!showActivitiesDropdown)}
                          aria-label="Select"
                          role="button"
                        >
                          {selectedActivities.length === 1 && `${selectedActivities.length} filter selected`}
                          {selectedActivities.length > 1 && `${selectedActivities.length} filters selected`}
                        </span>
                        <DropdownIcon className={styles.arrowIcon} />

                        {showActivitiesDropdown && (
                          <div className={styles.dropdownContainer}>
                            <CheckboxInputGroupAccessibleWDP title="Activities" options={allActivities} selectedValues={selectedActivities} onSelectionChange={setSelectedActivities} />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={styles.filterContainer}>
                      <div className={styles.searchTitle3}>Services for:</div>
                      <div ref={serviceDropdownRef} id="sponsor-services-dropdown" className={styles.selectContainer}>
                        <span
                          className={styles.checkboxSelect}
                          id="services"
                          title="Services"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === ' ' || e.key === 'Enter') {
                              setShowServicesDropdown(!showServicesDropdown);
                            }
                          }}
                          onClick={() => setShowServicesDropdown(!showServicesDropdown)}
                          aria-label="Select"
                          role="button">
                          {selectedServices.length === 1 && `${selectedServices.length} filter selected`}
                          {selectedServices.length > 1 && `${selectedServices.length} filters selected`}
                        </span>
                        <DropdownIcon className={styles.arrowIcon} />

                        {showServicesDropdown && (
                          <div className={styles.dropdownContainer2}>
                            <CheckboxInputGroupAccessibleWDP title="Services" options={allServices} selectedValues={selectedServices} onSelectionChange={setSelectedServices} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={styles.filterButtons}>
                    <div id="sponsor-telehealth-checkbox">
                      <Checkbox title="Show only Telehealth sponsors" state={telehealthOnly} setState={setTelehealthOnly} />
                    </div>
                    <div className={styles.filterButtonContainer}>
                      <Button size="small" onClick={clearButtonClick} color="transparent">
                        Clear
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            />
          </div>
        )
          : (
            <div className={styles.filterBoxContainer}>
              <div className={styles.searchTitle2}>Filter search results</div>
              <div className={styles.filtersContainer}>
                <div className={styles.filterContainer}>
                  <div className={styles.searchTitle3}>Type of activities they offer:</div>
                  <div ref={activityDropdownRef} id="sponsor-activities-dropdown" className={styles.selectContainer}>
                    <span
                      className={styles.checkboxSelect}
                      id="activity"
                      title="Activity"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === ' ' || e.key === 'Enter') {
                          setShowActivitiesDropdown(!showActivitiesDropdown);
                        }
                      }}
                      onClick={() => setShowActivitiesDropdown(!showActivitiesDropdown)}
                      aria-label="Select"
                      role="button"
                    >
                      {selectedActivities.length === 1 && `${selectedActivities.length} filter selected`}
                      {selectedActivities.length > 1 && `${selectedActivities.length} filters selected`}
                    </span>
                    <DropdownIcon className={styles.arrowIcon} />

                    {showActivitiesDropdown && (
                      <div className={styles.dropdownContainer}>
                        <CheckboxInputGroupAccessibleWDP title="Activities" options={allActivities} selectedValues={selectedActivities} onSelectionChange={setSelectedActivities} />
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.filterContainer}>
                  <div className={styles.searchTitle3}>Services for:</div>
                  <div ref={serviceDropdownRef} id="sponsor-services-dropdown" className={styles.selectContainer}>
                    <span
                      className={styles.checkboxSelect}
                      id="services"
                      title="Services"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === ' ' || e.key === 'Enter') {
                          setShowServicesDropdown(!showServicesDropdown);
                        }
                      }}
                      onClick={() => setShowServicesDropdown(!showServicesDropdown)}
                      aria-label="Select"
                      role="button">
                      {selectedServices.length === 1 && `${selectedServices.length} filter selected`}
                      {selectedServices.length > 1 && `${selectedServices.length} filters selected`}
                    </span>
                    <DropdownIcon className={styles.arrowIcon} />

                    {showServicesDropdown && (
                      <div className={styles.dropdownContainer2}>
                        <CheckboxInputGroupAccessibleWDP title="Services" options={allServices} selectedValues={selectedServices} onSelectionChange={setSelectedServices} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className={styles.filterButtons}>
                <div id="sponsor-telehealth-checkbox">
                  <Checkbox title="Show only Telehealth sponsors" state={telehealthOnly} setState={setTelehealthOnly} />
                </div>
                <div className={styles.filterButtonContainer}>
                  <Button size="small" onClick={clearButtonClick} color="transparent">
                    Clear
                  </Button>
                </div>
              </div>
            </div>
          )}
        {/* {error && <Alert icon={IssueIcon} color="red" message={error} theme="error" />} */}
      </div>
      <div>
        {loading ? (
          <div className={styles.loading}>
            <div className={`${styles.cssloadcontainer} ${styles.inline}`}>
              <div className={styles.cssloadwhirlpool} />
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: '20px' }}>
            {showFilterResult && !error && <SearchResults results={filteredData} searchInput={searchedVal} noResultsError={NO_RESULTS_ERROR_MESSAGE} />}
            {showSearchResult && !error && <SearchResults results={searchedData} searchInput={searchedVal} noResultsError={NO_RESULTS_ERROR_MESSAGE} />}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchBox;
